import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import {
  Baby, Users, Wand2, Building2, GraduationCap, ShieldCheck, ArrowRight, Sparkles,
  Mic, Gamepad2, Share2, Rocket, Check, ChevronRight, Loader2, BookOpen, Download,
} from "lucide-react";
import Logo from "@/components/Logo";
import LaunchTrustBadges from "@/components/LaunchTrustBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useMode } from "@/context/ModeContext";
import { useAuth } from "@/context/AuthContext";

// Audience-targeted marketing surfaces — copy sourced from the GameView Studio marketing plan (§3/§5).
const AUDIENCES = {
  kids: {
    icon: Baby, grad: "from-amber-400 to-orange-500",
    label: "Kids", eyebrow: "Ages 5–12",
    tagline: "Imagine it. Say it. Play it.",
    intro: "Kids create characters, worlds and adventures using voice commands, pictures and simple visual tools — no coding, no complicated software. Just imagination.",
    features: [
      "Kid-friendly creation mode", "Voice-first game building", "Large, easy visual controls",
      "Parent-linked accounts", "Safe, moderated publishing", "A friendly, moderated community", "Zero code required",
    ],
    prompts: ["A puppy that flies and collects stars", "A dragon who is scared of flying", "A rainbow kart race on the moon"],
    cta: "Start creating free",
  },
  teens: {
    icon: Users, grad: "from-[#3b9eff] to-[#1f6feb]",
    label: "Teen Creators", eyebrow: "Ages 13–17",
    tagline: "Build the game everyone at school wants to play.",
    intro: "Create games, team up with friends, grow a community, enter tournaments and start building real digital skills that go on your portfolio.",
    features: [
      "Multiplayer creation", "Friends, profiles & clans", "Competitions & achievements",
      "Creator recognition & levels", "Custom characters & worlds", "Game analytics", "Safe monetisation controls",
    ],
    prompts: ["A 2-player parkour race through a neon city", "A survival game where the weather hunts you", "A rhythm boss-battle set to my favourite beat"],
    cta: "Create my game",
  },
  creators: {
    icon: Wand2, grad: "from-[#a855f7] to-[#7c3aed]",
    label: "Beginners & Indie Creators", eyebrow: "Solo makers",
    tagline: "Your first game starts with one prompt.",
    intro: "Go from an idea to a playable game without hiring a development team or learning to code. Keep prompting to grow it into an entire universe.",
    features: [
      "AI Game Builder", "Prompt-to-game creation", "Ready-to-use templates", "AI characters & environments",
      "Gameplay mechanics generation", "Level creation & live preview", "One-click publishing", "Creator monetisation",
    ],
    prompts: ["A cosy shop-keeping game in a mushroom village", "A roguelike where every run remixes the map", "A detective mystery inside an underwater city"],
    cta: "Build my game",
  },
  studios: {
    icon: Building2, grad: "from-[#22c55e] to-[#14b8a6]",
    label: "Professional Creators & Studios", eyebrow: "Teams & pros",
    tagline: "Prototype faster. Build smarter. Launch more.",
    intro: "Accelerate concept development, world building, level creation, asset production, play testing and publishing with AI — then refine in the full studio.",
    features: [
      "Advanced Creator Mode", "Rapid AI prototyping", "AI play testing", "Difficulty balancing",
      "Asset management", "Marketplace access", "Commercial publishing tools",
    ],
    soonFeatures: ["Team members & collaborative projects", "Version history timeline"],
    prompts: ["A vertical-slice prototype for a co-op extraction shooter", "A tower-defence sandbox to test 3 economy models", "A boss-rush demo to validate our art direction"],
    cta: "Start prototyping",
  },
  schools: {
    icon: GraduationCap, grad: "from-violet-400 to-indigo-500",
    label: "Schools & Educators", eyebrow: "Classrooms",
    tagline: "Turn students from game players into game creators.",
    intro: "Introduce students to storytelling, logic, design, problem-solving, collaboration and technology through game creation — inside a safe, controlled classroom.",
    features: [
      "Classroom accounts", "Teacher dashboards", "Student projects", "Safe publishing controls",
      "Group challenges", "Progress tracking", "Private classroom environments", "Parent & teacher oversight",
    ],
    program: ["Week 1 — Ideas, characters & storytelling", "Week 2 — World creation & gameplay", "Week 3 — Testing & problem-solving", "Week 4 — Publishing & classroom showcase"],
    cta: "Bring GameView to your school",
  },
  parents: {
    icon: ShieldCheck, grad: "from-emerald-400 to-teal-500",
    label: "Parents", eyebrow: "Family-safe",
    tagline: "More than screen time. Creative time.",
    intro: "Give children a structured environment where they use technology to design, solve problems, tell stories and create — with you in control.",
    features: [
      "Parent-linked accounts", "Time & access controls", "Publishing permissions", "Content moderation",
      "Privacy controls", "Progress visibility", "Age-appropriate experiences", "Real educational benefits",
    ],
    cta: "Explore parent controls",
  },
};

const HOW = [
  { icon: Mic, t: "Imagine it", d: "Start with an idea, drawing, voice command or written prompt." },
  { icon: Wand2, t: "Build it", d: "GameView Studio creates the world, characters, mechanics and objectives." },
  { icon: Gamepad2, t: "Play it", d: "Play instantly in your browser and get AI suggestions to improve." },
  { icon: Share2, t: "Publish it", d: "Release to the GameView Arcade and invite the world to play." },
];

export default function AudienceLanding() {
  const { audience } = useParams();
  const navigate = useNavigate();
  const { setMode } = useMode();
  const { user } = useAuth();
  useEffect(() => { setMode("studio"); }, [setMode]);
  useEffect(() => { window.scrollTo(0, 0); }, [audience]);

  const a = AUDIENCES[audience];
  const isSchool = audience === "schools";
  const [formOpen, setFormOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [leadSource, setLeadSource] = useState("demo");
  const [form, setForm] = useState({ school_name: "", contact_name: "", email: "", role: "", students: "", country: "", message: "" });
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  if (!a) return <Navigate to="/" replace />;
  const Icon = a.icon;
  const go = () => navigate(user ? "/app/create" : "/register");
  const openLead = (source) => { setLeadSource(source); setSent(false); setFormOpen(true); };
  const primaryClick = isSchool ? () => openLead("demo") : go;
  const isGuide = leadSource === "teacher_guide";

  const submitSchool = async () => {
    if (!form.school_name.trim() || !form.contact_name.trim()) { toast.error("Please add your school and name"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { toast.error("Please enter a valid email"); return; }
    setSending(true);
    try {
      await api.post("/leads/school", { ...form, source: leadSource });
      setSent(true);
      toast.success(isGuide ? "Your Teacher Guide is ready! 📘" : "Request received — we'll be in touch! 🎓");
    }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setSending(false); }
  };

  const downloadGuide = () => {
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>GameView Studio — Teacher Guide</title>
<style>body{font-family:system-ui,Arial,sans-serif;max-width:760px;margin:40px auto;padding:0 24px;color:#111;line-height:1.6}
h1{font-size:28px}h2{margin-top:28px;color:#5b21b6}li{margin:4px 0}.tag{color:#6b7280;font-size:13px}</style></head><body>
<h1>How Game Creation Develops Creativity, Logic &amp; Digital Skills</h1>
<p class="tag">A free teacher guide from GameView Studio — Create Any Game You Imagine.</p>
<h2>Why game creation in the classroom?</h2>
<p>When students move from playing games to <b>building</b> them, they practise storytelling, systems thinking, design, iteration and collaboration — no coding required.</p>
<h2>The "Build a Game in Four Weeks" program</h2>
<ul>
<li><b>Week 1 — Ideas, characters &amp; storytelling:</b> Students describe a game world and cast in their own words.</li>
<li><b>Week 2 — World creation &amp; gameplay:</b> They shape levels, mechanics and objectives with the AI Studio.</li>
<li><b>Week 3 — Testing &amp; problem-solving:</b> Play, gather feedback and refine — the core of computational thinking.</li>
<li><b>Week 4 — Publishing &amp; classroom showcase:</b> Publish safely and present to the class.</li>
</ul>
<h2>Skills mapped</h2>
<ul><li>Creativity &amp; narrative design</li><li>Logic &amp; sequencing</li><li>Iteration &amp; debugging mindset</li><li>Collaboration &amp; presentation</li><li>Responsible, age-appropriate technology use</li></ul>
<h2>Safety &amp; oversight</h2>
<p>Classroom accounts, teacher dashboards, private environments, safe publishing controls and moderation keep students protected throughout.</p>
<h2>Get started</h2>
<p>Request a classroom demo and trial at gameview.studio — our education team will help you set up.</p>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "GameView-Studio-Teacher-Guide.html";
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="min-h-screen bg-[#05060a] text-[hsl(var(--foreground))] overflow-x-hidden" data-testid={`audience-${audience}`}>
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo to="/" size="md" animated />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="audience-login-btn">Log in</Button>
            <Button onClick={() => navigate("/register")} data-testid="audience-getstarted-btn"
              className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
              Get Started <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 max-w-5xl mx-auto text-center">
        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 bg-gradient-to-r ${a.grad} text-white text-xs font-bold uppercase tracking-wider`} data-testid="audience-eyebrow">
          <Icon className="w-4 h-4" /> {a.label} · {a.eyebrow}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5" data-testid="audience-tagline">{a.tagline}</h1>
        <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto mb-8">{a.intro}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button size="lg" onClick={primaryClick} data-testid="audience-primary-cta"
            className="rounded-full text-base px-8 py-6 font-bold bg-gradient-to-r from-[#7c3aed] via-[#1f6feb] to-[#3b9eff] btn-glow">
            <Sparkles className="w-5 h-5" /> {a.cta} <ArrowRight className="w-4 h-4" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => navigate("/app/play")} className="rounded-full text-base px-8 py-6 border-white/20 text-white hover:bg-white/10" data-testid="audience-arcade-cta">
            <Gamepad2 className="w-5 h-5" /> Explore the Arcade
          </Button>
        </div>
        <p className="text-xs text-white/40 mt-4">No coding required. Create your first playable game in minutes.</p>
      </section>

      {/* What you can do */}
      <section className="relative max-w-6xl mx-auto px-5 py-10">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-10">What you can do</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {a.features.map((f) => (
            <div key={f} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid="audience-feature">
              <span className={`w-9 h-9 rounded-xl grid place-items-center bg-gradient-to-br ${a.grad} shrink-0`}><Check className="w-5 h-5 text-white" /></span>
              <span className="text-sm font-semibold text-white/90">{f}</span>
            </div>
          ))}
          {(a.soonFeatures || []).map((f) => (
            <div key={f} className="glass rounded-2xl p-4 flex items-center gap-3 opacity-80" data-testid="audience-feature-soon">
              <span className="w-9 h-9 rounded-xl grid place-items-center bg-white/10 shrink-0"><Rocket className="w-5 h-5 text-white/70" /></span>
              <span className="text-sm font-semibold text-white/70 flex-1">{f}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/60 uppercase tracking-wide">Coming soon</span>
            </div>
          ))}
        </div>
      </section>

      {/* Example prompts (creation-focused audiences) */}
      {a.prompts && (
        <section className="relative max-w-4xl mx-auto px-5 py-10">
          <div className="glass rounded-3xl p-8 relative overflow-hidden">
            <div className={`absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gradient-to-br ${a.grad} opacity-20 blur-3xl`} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-white/70 mb-4"><Wand2 className="w-4 h-4" /> TRY A PROMPT LIKE</div>
              <div className="space-y-3">
                {a.prompts.map((pr) => (
                  <button key={pr} onClick={() => navigate(user ? `/app/create?idea=${encodeURIComponent(pr)}` : "/register")}
                    data-testid="audience-prompt"
                    className="group w-full text-left rounded-xl border border-white/10 bg-white/[0.03] hover:border-[hsl(var(--primary))] hover:bg-white/[0.06] px-4 py-3 flex items-center justify-between transition-colors">
                    <span className="font-mono text-sm text-white/85">“{pr}”</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* School 4-week program */}
      {a.program && (
        <section className="relative max-w-5xl mx-auto px-5 py-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-3">Build your first game in four weeks</h2>
          <p className="text-center text-white/60 mb-8">A guided, project-based program for the classroom.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {a.program.map((w, i) => (
              <div key={w} className="glass rounded-2xl p-5" data-testid="audience-program-week">
                <div className={`w-9 h-9 rounded-xl grid place-items-center bg-gradient-to-br ${a.grad} text-white font-display font-extrabold mb-3`}>{i + 1}</div>
                <p className="text-sm text-white/80 font-medium">{w.split(" — ")[1]}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 glass rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left" data-testid="teacher-guide-cta">
            <div className="w-12 h-12 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-violet-500 to-indigo-500">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-white">Free Teacher Guide</h3>
              <p className="text-sm text-white/60">"How Game Creation Develops Creativity, Logic &amp; Digital Skills" — a ready-to-use classroom primer.</p>
            </div>
            <Button onClick={() => openLead("teacher_guide")} data-testid="teacher-guide-btn"
              className="rounded-full shrink-0 bg-gradient-to-r from-violet-500 to-indigo-500">
              <Download className="w-4 h-4" /> Get the guide
            </Button>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="relative max-w-6xl mx-auto px-5 py-8">
        <LaunchTrustBadges />
      </section>
      <section className="relative max-w-6xl mx-auto px-5 py-12">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-10">From idea to published — in minutes</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HOW.map((s, i) => (
            <div key={s.t} className="glass rounded-2xl p-6" data-testid="audience-how-step">
              <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] mb-4"><s.icon className="w-6 h-6 text-white" /></div>
              <div className="text-xs font-bold text-[#3b9eff] mb-1">STEP {i + 1}</div>
              <h3 className="font-display text-lg font-bold mb-2">{s.t}</h3>
              <p className="text-sm text-white/60">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative max-w-4xl mx-auto px-5 py-16 text-center">
        <div className="glass rounded-3xl p-10 sm:p-14 brand-glow">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4">The next great game could start with <span className="text-brand-blue">your idea.</span></h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">Describe it. Build it. Play it. Publish it.</p>
          <Button size="lg" onClick={primaryClick} data-testid="audience-bottom-cta"
            className="rounded-full text-base px-10 py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
            {a.cta} <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Other audiences */}
      <section className="relative max-w-5xl mx-auto px-5 pb-16">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-white/40 mb-5">GameView is built for everyone</p>
        <div className="flex flex-wrap justify-center gap-2">
          {Object.entries(AUDIENCES).filter(([k]) => k !== audience).map(([k, v]) => (
            <Link key={k} to={`/for/${k}`} data-testid={`audience-link-${k}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-white/80 hover:border-[hsl(var(--primary))] hover:text-white transition-colors">
              <v.icon className="w-4 h-4" /> {v.label.split(" ")[0]}
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-5 text-center text-sm text-white/40">
        <div className="flex justify-center mb-3"><Logo size="sm" animated /></div>
        GameView Studio — Create Any Game You Imagine.
      </footer>

      {/* School demo request / Teacher Guide lead capture (§14) */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setSent(false); }}>
        <DialogContent className="max-w-lg" data-testid="school-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isGuide ? <BookOpen className="w-5 h-5 text-violet-400" /> : <GraduationCap className="w-5 h-5 text-violet-400" />}
              {isGuide ? "Get the free Teacher Guide" : "Bring GameView to your school"}
            </DialogTitle>
            <DialogDescription>
              {isGuide ? "Tell us where to send it and download your copy instantly." : "Tell us about your classroom and we'll set up a demo and trial."}
            </DialogDescription>
          </DialogHeader>
          {sent ? (
            isGuide ? (
              <div className="text-center py-6" data-testid="teacher-guide-success">
                <div className="w-14 h-14 rounded-full bg-violet-500/20 grid place-items-center mx-auto mb-4"><BookOpen className="w-7 h-7 text-violet-400" /></div>
                <h3 className="font-display text-xl font-bold text-white mb-1">Your guide is ready! 📘</h3>
                <p className="text-sm text-white/60 mb-5">We also emailed a copy to <span className="font-semibold text-white">{form.email}</span>.</p>
                <Button onClick={downloadGuide} className="rounded-full bg-gradient-to-r from-violet-500 to-indigo-500" data-testid="teacher-guide-download">
                  <Download className="w-4 h-4" /> Download the guide
                </Button>
                <div><Button variant="ghost" onClick={() => setFormOpen(false)} className="rounded-full mt-3 text-white/60" data-testid="school-lead-close">Done</Button></div>
              </div>
            ) : (
              <div className="text-center py-8" data-testid="school-lead-success">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 grid place-items-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-400" /></div>
                <h3 className="font-display text-xl font-bold text-white mb-1">Request received! 🎓</h3>
                <p className="text-sm text-white/60">Our education team will reach out to <span className="font-semibold text-white">{form.email}</span> shortly.</p>
                <Button onClick={() => setFormOpen(false)} className="rounded-full mt-5" data-testid="school-lead-close">Done</Button>
              </div>
            )
          ) : (
            <div className="space-y-3 pt-1">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">School / organisation *</Label><Input value={form.school_name} onChange={setF("school_name")} className="mt-1" data-testid="school-field-name" /></div>
                <div><Label className="text-xs">Your name *</Label><Input value={form.contact_name} onChange={setF("contact_name")} className="mt-1" data-testid="school-field-contact" /></div>
                <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={setF("email")} className="mt-1" data-testid="school-field-email" /></div>
                <div><Label className="text-xs">Your role</Label><Input value={form.role} onChange={setF("role")} placeholder="Teacher, IT coordinator…" className="mt-1" data-testid="school-field-role" /></div>
                <div><Label className="text-xs"># of students</Label><Input value={form.students} onChange={setF("students")} placeholder="e.g. 120" className="mt-1" data-testid="school-field-students" /></div>
                <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={setF("country")} className="mt-1" data-testid="school-field-country" /></div>
              </div>
              <div><Label className="text-xs">Anything else?</Label><Textarea value={form.message} onChange={setF("message")} rows={3} className="mt-1" data-testid="school-field-message" /></div>
              <Button onClick={submitSchool} disabled={sending} className="w-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500" data-testid="school-lead-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (isGuide ? <>Get my guide <Download className="w-4 h-4" /></> : <>Request a demo <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
